import { cx } from '@emotion/css';
import { type ReactElement, type ReactNode } from 'react';
import Icon from '../Icon';
import { keyframes, type Keyframes } from '@emotion/react';

type Props = {
  /** Pass in a label  */
  children?: ReactNode;
  className?: string;
  /** Spinner color */
  color?: 'secondary' | 'white' | 'primary' | 'black';
  /** Size of the spinner and label */
  size?: 'sm' | 'md' | 'lg';
  /** Display inline */
  inline?: boolean;
  /** Stack the spinner and label */
  stacked?: boolean;
  /** Fill the container to center the spinner */
  fill?: boolean;
};

const SPIN_DURATION_MS = 2_000;

const getStartRotation = (): number => {
  const now = new Date();
  const s = now.getSeconds() * 1_000;
  const ms = now.getMilliseconds();
  const startRotation = (((s + ms) % SPIN_DURATION_MS) / SPIN_DURATION_MS) * 360;
  return startRotation;
};

const makeSpin = (): Keyframes => {
  const startRotation = getStartRotation();
  return keyframes`
    from {
      transform: rotate(${startRotation}deg);
    }
    to {
      transform: rotate(${startRotation + 360}deg);
    }
  `;
};

function ODSSpinner({
  children,
  className,
  color = 'secondary',
  size = 'md',
  inline = false,
  stacked,
  fill = !inline,
  ...props
}: Props): ReactElement {
  const spin = makeSpin();

  let fontSize = '0.875rem';

  switch (size) {
    case 'sm':
      fontSize = '0.75rem';
      break;
    case 'lg':
      fontSize = '1.125rem';
      break;
    default:
  }

  return (
    <div
      data-testid="spinner-ods-spinner"
      className={cx(
        `justify-content-center align-items-center flex-shrink-0 gap-1 user-select-none text-${color}`,
        className,
        inline ? 'd-inline-flex' : 'd-flex',
        { 'flex-column text-center': stacked, 'flex-grow-1': fill }
      )}
      css={{ fontSize }}
      {...props}
    >
      <div css={{ animation: `${spin} ${SPIN_DURATION_MS}ms linear infinite;` }}>
        <Icon name="radar-with-track" width="1.5em" className="d-block" />
      </div>
      {children && <div>{children}</div>}
    </div>
  );
}

const test = { getStartRotation };
export { test };

export default ODSSpinner;
